import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HiPlus, HiMinus, HiRefresh, HiOutlineViewGrid } from 'react-icons/hi';

/**
 * MermaidDiagram Component - Renders mermaid diagrams with zoom and pan functionality
 * @param {Object} props - Component props
 * @param {string} props.code - Mermaid code to render
 * @param {Function} props.onShowCode - Callback to switch to code view
 * @param {boolean} props.isStreaming - Whether the code content is still streaming
 */
const MermaidDiagram = ({ code, onShowCode, isStreaming = false }) => {
  // State management
  const [mermaidError, setMermaidError] = useState(null);
  const [isCompiling, setIsCompiling] = useState(false);
  const [mermaidLoaded, setMermaidLoaded] = useState(false);
  const [mermaidLib, setMermaidLib] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const [diagramId] = useState(() => `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  
  // Fullscreen state
  const [fullscreenZoom, setFullscreenZoom] = useState(1);
  const [fullscreenPan, setFullscreenPan] = useState({ x: 0, y: 0 });
  const [fullscreenDragging, setFullscreenDragging] = useState(false);
  const [fullscreenDragStart, setFullscreenDragStart] = useState({ x: 0, y: 0 });

  // Refs
  const mermaidRef = useRef(null);
  const containerRef = useRef(null);
  const fullscreenMermaidRef = useRef(null);
  const fullscreenContainerRef = useRef(null);

  // Load mermaid library
  useEffect(() => {
    if (!mermaidLoaded) {
      const loadMermaid = async () => {
        try {
          const mermaidModule = await import('mermaid');
          const mermaid = mermaidModule.default || mermaidModule;
          
          mermaid.initialize({
            startOnLoad: false,
            theme: 'default',
            securityLevel: 'loose',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 14,
            flowchart: { useMaxWidth: true, htmlLabels: true },
            sequence: { useMaxWidth: true, wrap: true },
            gantt: { useMaxWidth: true },
            themeVariables: {
              primaryColor: '#3B82F6',
              primaryTextColor: '#1F2937',
              primaryBorderColor: '#E5E7EB',
              lineColor: '#6B7280',
              secondaryColor: '#F3F4F6',
              tertiaryColor: '#F9FAFB'
            }
          });
          
          setMermaidLib(mermaid);
          setMermaidLoaded(true);
          setMermaidError(null);
        } catch (error) {
          console.error('Failed to load mermaid:', error);
          setMermaidError('Mermaid library not found. Please install it with: npm install mermaid');
        }
      };
      
      loadMermaid();
    }
  }, [mermaidLoaded]);

  // Render mermaid diagram
  const renderDiagram = useCallback(async (targetRef) => {
    if (mermaidLib && targetRef.current && code && !isStreaming) {
      setIsCompiling(true);
      const elementId = diagramId;
      try {
        const isValid = await mermaidLib.parse(code);
        if (!isValid) throw new Error('Invalid mermaid syntax');
        
        const { svg } = await mermaidLib.render(elementId, code);
        
        if (targetRef.current) {
          targetRef.current.innerHTML = svg;
          setMermaidError(null);
        }
      } catch (error) {
        console.error('Mermaid rendering error:', error);
        setMermaidError(error.message || 'Failed to render diagram');
        
        // On render error, Mermaid can inject an error SVG into the DOM body.
        // We need to clean this up to prevent it from cluttering the main page.
        // The ID of the injected container is typically 'd' + the elementId used for rendering.
        const errorElementId = `d${elementId}`;
        const errorElement = document.getElementById(errorElementId);
        if (errorElement) {
          errorElement.remove();
        }
      } finally {
        setIsCompiling(false);
      }
    }
  }, [mermaidLib, code, diagramId, isStreaming]);

  useEffect(() => {
    if (!isStreaming) {
      renderDiagram(mermaidRef);
    }
  }, [renderDiagram, isStreaming]);

  // Render fullscreen diagram when entering fullscreen
  useEffect(() => {
    if (isFullscreen && fullscreenMermaidRef.current && !isStreaming) {
      renderDiagram(fullscreenMermaidRef);
    }
  }, [isFullscreen, renderDiagram, isStreaming]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !mermaidLib || !code || isStreaming) return;

    let resizeObserver = null;
    let intersectionObserver = null;
    let debounceId = null;

    const requestRender = () => {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        try { renderDiagram(mermaidRef); } catch {}
      }, 120);
    };

    // Render once on next frame as an immediate attempt
    try { requestAnimationFrame(() => requestRender()); } catch { requestRender(); }

    // Re-render when element becomes visible in viewport
    try {
      intersectionObserver = new IntersectionObserver((entries) => {
        const entry = entries && entries[0];
        if (entry && entry.isIntersecting) requestRender();
      }, { threshold: 0.1 });
      intersectionObserver.observe(container);
    } catch {}

    // Re-render on size changes (including from width/height 0 -> >0)
    try {
      resizeObserver = new ResizeObserver((entries) => {
        const rect = entries && entries[0] && entries[0].contentRect;
        if (rect && rect.width > 0 && rect.height > 0) requestRender();
      });
      resizeObserver.observe(container);
    } catch {}

    const handleWindowResize = () => requestRender();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      try { if (intersectionObserver) intersectionObserver.disconnect(); } catch {}
      try { if (resizeObserver) resizeObserver.disconnect(); } catch {}
      if (debounceId) clearTimeout(debounceId);
    };
  }, [mermaidLib, code, isStreaming, renderDiagram]);

  // Re-render main diagram when exiting fullscreen
  useEffect(() => {
    if (!isFullscreen && mermaidRef.current && !isStreaming) {
      // Small delay to ensure DOM is ready after fullscreen exit
      setTimeout(() => {
        renderDiagram(mermaidRef);
      }, 100);
    }
  }, [isFullscreen, renderDiagram, isStreaming]);

  // Main view zoom and pan handlers (no scroll zoom)
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - pan.x,
      y: e.clientY - pan.y
    });
  }, [pan]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Fullscreen zoom and pan handlers (with scroll zoom)
  const handleFullscreenWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setFullscreenZoom(prev => Math.max(0.1, Math.min(5, prev + delta)));
  }, []);

  const handleFullscreenMouseDown = useCallback((e) => {
    setFullscreenDragging(true);
    setFullscreenDragStart({
      x: e.clientX - fullscreenPan.x,
      y: e.clientY - fullscreenPan.y
    });
  }, [fullscreenPan]);

  const handleFullscreenMouseMove = useCallback((e) => {
    if (!fullscreenDragging) return;
    setFullscreenPan({
      x: e.clientX - fullscreenDragStart.x,
      y: e.clientY - fullscreenDragStart.y
    });
  }, [fullscreenDragging, fullscreenDragStart]);

  const handleFullscreenMouseUp = useCallback(() => {
    setFullscreenDragging(false);
  }, []);

  // Touch handlers for main view
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - pan.x,
      y: touch.clientY - pan.y
    });
  }, [pan]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || e.touches.length !== 1) return;
    e.preventDefault();
    const touch = e.touches[0];
    setPan({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Zoom control functions for main view
  const zoomIn = useCallback(() => {
    setZoom(prev => Math.min(3, prev + 0.2));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom(prev => Math.max(0.1, prev - 0.2));
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Zoom control functions for fullscreen view
  const fullscreenZoomIn = useCallback(() => {
    setFullscreenZoom(prev => Math.min(5, prev + 0.2));
  }, []);

  const fullscreenZoomOut = useCallback(() => {
    setFullscreenZoom(prev => Math.max(0.1, prev - 0.2));
  }, []);

  const resetFullscreenZoom = useCallback(() => {
    setFullscreenZoom(1);
    setFullscreenPan({ x: 0, y: 0 });
  }, []);

  // Fullscreen functions
  const openFullscreen = useCallback(() => {
    setIsFullscreen(true);
    setFullscreenZoom(1);
    setFullscreenPan({ x: 0, y: 0 });
  }, []);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
    setFullscreenDragging(false);
  }, []);

  // Handle escape key to close fullscreen
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        closeFullscreen();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isFullscreen, closeFullscreen]);

  // Mouse leave handlers
  useEffect(() => {
    const handleMouseLeave = () => setIsDragging(false);
    const container = containerRef.current;
    
    if (container) {
      container.addEventListener('mouseleave', handleMouseLeave);
      return () => container.removeEventListener('mouseleave', handleMouseLeave);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenMouseLeave = () => setFullscreenDragging(false);
    const container = fullscreenContainerRef.current;
    
    if (container) {
      container.addEventListener('mouseleave', handleFullscreenMouseLeave);
      return () => container.removeEventListener('mouseleave', handleFullscreenMouseLeave);
    }
  }, []);

  if (!mermaidLoaded && mermaidError) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4">
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          <strong>Mermaid not available:</strong> {mermaidError}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Main View */}
      <div className="relative h-[500px] min-h-[300px] bg-white dark:bg-gray-800 rounded overflow-hidden">
        {/* Zoom Controls */}
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1 shadow-lg">
          <button
            onClick={zoomIn}
            disabled={zoom >= 3}
            className="flex items-center justify-center w-7 h-7 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-300"
            title="Zoom In"
          >
            <HiPlus className="w-4 h-4" />
          </button>
          
          <span className="min-w-[40px] text-center text-xs font-medium text-gray-600 dark:text-gray-300 px-1">
            {Math.round(zoom * 100)}%
          </span>
          
          <button
            onClick={zoomOut}
            disabled={zoom <= 0.1}
            className="flex items-center justify-center w-7 h-7 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-300"
            title="Zoom Out"
          >
            <HiMinus className="w-4 h-4" />
          </button>
          
          <button
            onClick={resetZoom}
            className="flex items-center justify-center w-7 h-7 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-300"
            title="Reset Zoom"
          >
            <HiRefresh className="w-4 h-4" />
          </button>
          
          <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1"></div>
          
          <button
            onClick={openFullscreen}
            className="flex items-center justify-center w-7 h-7 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-300"
            title="View Fullscreen"
          >
            <HiOutlineViewGrid className="w-4 h-4" />
          </button>
        </div>

        {/* Diagram Container - NO SCROLL ZOOM */}
        <div
          ref={containerRef}
          className={`w-full h-full overflow-hidden relative bg-white dark:bg-gray-800 flex items-center justify-center ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {mermaidError ? (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-w-[90%] p-4 border-2 border-dashed border-red-400 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-center">
              <p className="text-red-500 mb-2 font-medium">Failed to render Mermaid diagram</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{mermaidError}</p>
              <button
                onClick={onShowCode}
                className="text-blue-500 hover:text-blue-700 text-sm underline transition-colors"
              >
                View source code instead
              </button>
            </div>
          ) : (
            <div
              ref={mermaidRef}
              className="select-none transition-transform duration-150 ease-out flex items-center justify-center min-w-full min-h-full bg-white"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center'
              }}
            />
          )}
        </div>

        {/* Loading State */}
        {(!mermaidLoaded || isStreaming || isCompiling) && !mermaidError && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80">
            <div className="text-center">
              <div className="mb-2">
                <svg className="animate-spin h-8 w-8 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <div className="text-gray-500 dark:text-gray-400 text-sm">
                {!mermaidLoaded ? 'Loading Mermaid library...' : isStreaming ? 'Waiting for full code...' : 'Compiling diagram...'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
          <div className="relative w-full h-full bg-white dark:bg-gray-900">
            {/* Fullscreen Header */}
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 px-4 py-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Mermaid Diagram - Fullscreen View</h3>
              
              {/* Fullscreen Zoom Controls */}
              <div className="flex items-center gap-1">
                <button
                  onClick={fullscreenZoomIn}
                  disabled={fullscreenZoom >= 5}
                  className="flex items-center justify-center w-8 h-8 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-300"
                  title="Zoom In"
                >
                  <HiPlus className="w-4 h-4" />
                </button>
                
                <span className="min-w-[50px] text-center text-sm font-medium text-gray-600 dark:text-gray-300 px-2">
                  {Math.round(fullscreenZoom * 100)}%
                </span>
                
                <button
                  onClick={fullscreenZoomOut}
                  disabled={fullscreenZoom <= 0.1}
                  className="flex items-center justify-center w-8 h-8 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-300"
                  title="Zoom Out"
                >
                  <HiMinus className="w-4 h-4" />
                </button>
                
                <button
                  onClick={resetFullscreenZoom}
                  className="flex items-center justify-center w-8 h-8 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-300 mr-2"
                  title="Reset Zoom"
                >
                  <HiRefresh className="w-4 h-4" />
                </button>
                
                <button
                  onClick={closeFullscreen}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Fullscreen Diagram Container - WITH SCROLL ZOOM */}
            <div
              ref={fullscreenContainerRef}
              className={`w-full h-full pt-12 overflow-hidden relative bg-white flex items-center justify-center ${
                fullscreenDragging ? 'cursor-grabbing' : 'cursor-grab'
              }`}
              onWheel={handleFullscreenWheel}
              onMouseDown={handleFullscreenMouseDown}
              onMouseMove={handleFullscreenMouseMove}
              onMouseUp={handleFullscreenMouseUp}
            >
              <div
                ref={fullscreenMermaidRef}
                className="select-none transition-transform duration-150 ease-out flex items-center justify-center min-w-full min-h-full"
                style={{
                  transform: `translate(${fullscreenPan.x}px, ${fullscreenPan.y}px) scale(${fullscreenZoom})`,
                  transformOrigin: 'center center'
                }}
              />
            </div>

           
          </div>
        </div>
      )}
    </>
  );
};

export default MermaidDiagram; 